import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { groupTopicsBySections } from './GroupTopics';

import RenameDialog from './RenameDialog';
import DuplicateDialog from './DuplicateDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';

const TopicsList = ({
  topics = [],
  isLoading,
  selectedTopic,
  onTopicClick,
  onDeleteTopic,
  onRenameTopic,
  onDuplicateTopic,
  sentinelRef,
  loadingMore,
}) => {
  // 3-dot menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuTopic, setMenuTopic] = useState(null);

  // Dialog states
  const [titleDialogOpen, setTitleDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const sections = groupTopicsBySections(topics);

  const handleMenuOpen = (event, topic) => {
    event.stopPropagation(); // prevent the item’s onClick from firing
    setMenuAnchorEl(event.currentTarget);
    setMenuTopic(topic);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Menu item handlers
  const handleRenameClick = () => {
    if (!menuTopic) return;
    setNewTitle(menuTopic.title || '');
    setTitleDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (!menuTopic) return;
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDuplicateClick = () => {
    if (!menuTopic) return;
    setNewTitle(menuTopic.title || '');
    setDuplicateDialogOpen(true);
    handleMenuClose();
  };

  // Dialog confirmations
  const handleRenameConfirm = () => {
    onRenameTopic(menuTopic.researchID, newTitle);
    setTitleDialogOpen(false);
    setNewTitle('');
    setMenuTopic(null);
  };

  const handleDeleteConfirm = () => {
    onDeleteTopic(menuTopic.researchID);
    setDeleteDialogOpen(false);
    setMenuTopic(null);
  };

  const handleDuplicateConfirm = () => {
    onDuplicateTopic(menuTopic.researchID, newTitle);
    setDuplicateDialogOpen(false);
    setNewTitle('');
    setMenuTopic(null);
  };

  // Dialog cancellations
  const handleRenameCancel = () => {
    setTitleDialogOpen(false);
    setNewTitle('');
    setMenuTopic(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMenuTopic(null);
  };

  const handleDuplicateCancel = () => {
    setDuplicateDialogOpen(false);
    setNewTitle('');
    setMenuTopic(null);
  };

  // Loading or no topics
  if (isLoading) {
    return <div className="textbox container" style={{ backgroundColor: 'transparent', color: 'var(--background-color)' }}>Loading topics...</div>;
  }
  if (topics.length === 0) {
    return <div className="textbox container" style={{ backgroundColor: 'transparent', color: 'var(--background-color)' }}>No topics available.</div>;
  }

  // Render
  return (
    <div className="topic-list">
      {sections.map((section) => {
        if (section.topics.length === 0) return null;
        return (
          <div key={section.label} style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0.5rem 0', color: 'var(--background-color)' }}>
              {section.label}
            </h3>
            <ul>
              {section.topics.map((topic) => (
                <li
                  key={topic.researchID}
                  onClick={() => onTopicClick(topic)}
                  className={`
                    ${topic.isPremium ? 'premium-topic' : 'container'} 
                    ${selectedTopic?.researchID === topic.researchID ? 'selected-topic' : ''}
                  `}
                >
                  <span>{topic.title}</span>
                  <div className="topic-actions">
                    {topic.isPremium && <span className="premium-star">★</span>}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '5px',
                        backgroundColor: 'var(--tertiary-color)',
                        cursor: 'pointer',
                      }}
                    >
                      <MoreVertIcon
                        onClick={(e) => handleMenuOpen(e, topic)}
                        sx={{
                          color: 'var(--background-color)',
                          '&:hover': { color: 'black' },
                        }}
                      />
                    </Box>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      {/* Shared MUI Menu for 3-dot usage */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { mt: 1, boxShadow: 3, borderRadius: 2 },
        }}
      >
        <MenuItem onClick={handleRenameClick}>Rename</MenuItem>
        <MenuItem onClick={handleDuplicateClick}>Duplicate</MenuItem>
        <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <RenameDialog
        open={titleDialogOpen}
        onCancel={handleRenameCancel}
        onConfirm={handleRenameConfirm}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
      />

      {/* Duplicate Dialog */}
      <DuplicateDialog
        open={duplicateDialogOpen}
        onCancel={handleDuplicateCancel}
        onConfirm={handleDuplicateConfirm}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
      />

      {/* Delete Confirm Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      {loadingMore && (
        <div style={{ display:'flex', justifyContent:'center', padding: '1rem' }}>
          <CircularProgress size={30} />
        </div>
      )}

      {/* sentinel for infinite scroll – invisible */} 
      {sentinelRef && <div ref={sentinelRef} style={{ height: 10 }} />}
    </div>
  );
};

export default TopicsList;
